import React, { useState } from 'react'
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'

import Modal from '../../../../components/Modal'
import { Button } from '../../../../components/Button'
import TextField from '../../../../components/TextField'
import { useNavigate } from 'react-router-dom'
import { TestIds } from '../../../../testIds'
import { Loader } from '../../../../components/Loader'
import { useSelector } from 'react-redux'
import {
  selectAllFarmerAccounts,
  selectCurrentFarmerAccount,
  selectIsThisTfo,
} from '../../../azure-b2c/storage'
import { CartItem } from '../../../cart/storage/cart.types'
import {
  OfferQuotePayload,
  Quote,
  QuoteProductInfo,
  SearchQuotesResponse,
} from '../../storage/types'
import apiClient from '../../../../services/api-client'
import useToast from '../../../../util/useToast'
import { selectStoreById } from '../../../stores/storage/stores.slice'
import { PadActionButton } from '../../../../components/Pad/Pad'
import { ExpiresOnDatePicker } from '../QuoteOffer/components/ExpiresOnDateSelector'
import { FreightChargeInput } from '../QuoteOffer/components/FreightChargeInput'
import { RestrictedCheckbox } from '../QuoteOffer/components/RestrictedCheckbox'
import { createColdOffer } from '../../storage/api/createColdOffer'
import { MultiAccountSelector } from '../QuoteOffer/components/MultiAccountSelector'
import { useAppDispatch } from '../../../../store/rootStore'
import CartSlice from '../../../cart/storage/cart.slice'

export interface SendOfferModalProps {
  isModalOpen: boolean
  setIsModalOpen: (open: boolean) => void
  quote?: Quote
  products: CartItem[]
  // If cold, send to quote/offer api, not /quote/{id}/offer
  cold?: boolean
  multiple?: boolean
}

export const SendOfferModal = ({
  isModalOpen = false,
  setIsModalOpen,
  quote,
  products,
  cold = false,
  multiple = false,
}: SendOfferModalProps) => {
  const account = useSelector(selectCurrentFarmerAccount)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [accountList, setAccountList] = React.useState([account!])

  const isTfo = useSelector(selectIsThisTfo)

  const [note, setNote] = useState<string | null>(null)
  const [expiryDate, setExpiryDate] = React.useState<dayjs.Dayjs | undefined>(
    dayjs().add(13, 'days').endOf('day'),
  )
  const [dateError, setDateError] = React.useState<string>()
  const [freightCharge, setFreightCharge] = React.useState<string>('0.00')
  const [freightChargePrice, setFreightChargePrice] = React.useState<number>(0)

  const [isLocked, setIsLocked] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)

  const navigate = useNavigate()
  const { notify } = useToast()
  const dispatch = useAppDispatch()

  async function tfoCreateColdOffer() {
    if (account && isTfo && expiryDate) {
      setError(false)
      setLoading(true)
      try {
        const quoteProduct = products.map((e) => {
          return {
            ...e,
            itemId: e.itemNumber,
            editedPriceExcGst: e.editedPriceExcGst,
          } as QuoteProductInfo
        })

        const response = await createColdOffer({
          accountNumbers: accountList.map((account) => account.accountNumber),
          isLocked: isLocked,
          delivery: {
            freightChargeExcGst: freightChargePrice,
          },
          expiresOn: expiryDate.toDate(),
          note: note ?? '',
          products: [...quoteProduct],
        })

        if (response.status.toString().startsWith('2')) {
          setLoading(false)
          setError(false)
          notify('Quote sent')
          setIsModalOpen(false)
          dispatch(CartSlice.actions.clearCart({ accountNumber: account.accountNumber }))
        } else {
          setLoading(false)
          setError(true)
        }
      } catch (error) {
        console.log(error)
        setLoading(false)
        setError(true)
      }
    }
  }

  const onSubmit = () => {
    if (cold) {
      tfoCreateColdOffer()
    } else {
      tfoRespondWithOffer()
    }
  }

  async function tfoRespondWithOffer(): Promise<void> {
    try {
      setLoading(true)
      if (!quote?.version || !account?.accountNumber) return

      const payload: OfferQuotePayload = {
        version: quote?.version,
        accountNumber: account?.accountNumber,
        products,
        expiresOn: dayjs(expiryDate).endOf('day').toDate(),
        isLocked,
        delivery: {
          ...quote?.delivery,
          freightChargeExcGst: freightChargePrice,
        },
        note,
      }

      const result = await apiClient.post<SearchQuotesResponse, OfferQuotePayload>(
        `/quotes/${quote.id}/offer`,
        payload,
      )
      if (result && result.status === 200 && result.data) {
        notify('Quote sent')
        navigate('/quotes')
      } else {
        setError(true)
      }
    } catch (error) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const pickupStore = useSelector(selectStoreById(quote?.delivery.pickupStoreId ?? undefined))

  const renderEditPickUpStoreButton = React.useMemo(() => {
    const onClick = () => {
      //TODO:Add behaviour if TFO can edit the delivery
    }

    if (pickupStore) {
      const { orgUnitName, address } = pickupStore
      return (
        <PadActionButton
          onClick={onClick}
          buttonTestId={TestIds.feat.quotes.sendOffer.location}
          title='Pick up from:'
          subtitle={
            <Typography variant='body2' component='span'>
              <Typography variant='body2' component='p' fontWeight={700}>
                {orgUnitName}
              </Typography>
              {address}
            </Typography>
          }
          isEditEnabled={false}
        />
      )
    }
    if (quote?.delivery.deliveryAddress) {
      const { streetAddress, name, city, postcode } = quote.delivery.deliveryAddress
      return (
        <PadActionButton
          onClick={onClick}
          buttonTestId={TestIds.components.address.editButton}
          title='Deliver to:'
          subtitle={
            <Typography variant='body2' component='span'>
              <Typography variant='body2' component='p' fontWeight={700}>
                {name}
              </Typography>
              {streetAddress}
              <br />
              {`${city}, ${postcode}`}
            </Typography>
          }
          isEditEnabled={false}
        />
      )
    }
  }, [pickupStore])

  const accounts = useSelector(selectAllFarmerAccounts)
  const farmerAccounts = React.useMemo(() => {
    if (accounts?.accounts) {
      const keys = Object.keys(accounts?.accounts)
      return keys.map((e) => accounts.accounts[e])
    }

    return []
  }, [accounts])

  const getMultiAccountSelector = () => {
    return (
      <MultiAccountSelector
        accountList={accountList}
        setAccountList={setAccountList}
        farmerAccounts={farmerAccounts}
      />
    )
  }

  const getFooter = () => {
    if (loading) {
      return <Loader title='Sending order' type='inline-flex' sx={{ ml: 'auto' }} />
    } else {
      return (
        <Button variant='contained' onClick={onSubmit} sx={{ width: isSm ? '100%' : 'auto' }}>
          Send quote
        </Button>
      )
    }
  }

  return (
    <Modal
      title='Send Quote'
      open={isModalOpen}
      maxWidth='sm'
      handleClose={() => setIsModalOpen(false)}
      footer={getFooter()}
      paperProps={{
        sx: {
          height: isSm ? 'inherit' : 'auto',
        },
      }}
    >
      <Stack spacing={4} sx={{ overflowX: 'hidden' }}>
        {multiple && getMultiAccountSelector()}
        <ExpiresOnDatePicker
          key='ExpiresOnDatePicker'
          dateError={dateError}
          expiresOn={expiryDate}
          setDateError={setDateError}
          setExpiresOn={setExpiryDate}
        />
        <RestrictedCheckbox
          key='RestrictedCheckbox'
          restricted={isLocked}
          setRestricted={setIsLocked}
        />
        <FreightChargeInput
          key='FreightChargeInput'
          freightCharge={freightCharge}
          setFreightCharge={setFreightCharge}
          setFreightChargePrice={setFreightChargePrice}
        />
        {renderEditPickUpStoreButton}
        <Box sx={{ minHeight: 100 }}>
          <TextField
            aria-label='Notes'
            label='Notes'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 200 }}
            maxRows={10}
            multiline
            minRows={3}
            variant='outlined'
            helperText='Notes will be sent to the account holder'
            testId={TestIds.feat.quotes.sendOffer.note}
          />
        </Box>
      </Stack>

      {error && (
        <Typography variant='body2' color='error'>
          Something went wrong, please try again.
        </Typography>
      )}
    </Modal>
  )
}
